/**
 * Familienanzeige (27.10.2026): was das Küchentablet zeigt.
 *
 * Kein eigener Kontotyp, sondern ein Anzeigemodus in der App eines
 * Erwachsenen. Eine Küche hat Gäste — gezeigt wird deshalb **nur Geteiltes
 * des Haushalts**, das jeder sehen darf: Haushaltstermine, offene Aufgaben
 * aus geteilten Listen, die Einkaufsliste und die Sterne der Kinder.
 * Nie: eigene Termine, private Listen, Körperwerte, Training, Ernährung,
 * Tagebuch, Kosten, Taschengeld (auch kein Geld an Aufgaben), Notizen über
 * Kinder. Die Auswahl steht hier als reine Funktion (`familyBoard`) — sie
 * entscheidet über die Feldnamen, die Anzeige bekommt nichts anderes.
 */

import { addDays, shelfOrder, starBalance, taskState } from '@kraftwerk/shared';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalHouseholdEvent,
  LocalHouseholdMember,
  LocalShoppingItem,
  LocalStarEntry,
  LocalTask,
  LocalTaskList,
} from './local.js';

/** Wie viele Namen der Einkaufsliste zu sehen sind — der Rest nur als Zahl. */
export const BOARD_SHOPPING_NAMES = 5;

export interface BoardEvent {
  id: string;
  title: string;
  date: string;
  /** `HH:MM`, `null` = ganztägig. */
  time: string | null;
  location: string | null;
  cancelled: boolean;
  /** Namen der Mitglieder, für die der Termin ist. */
  forNames: string[];
}

export interface BoardTask {
  id: string;
  title: string;
  dueDate: string;
  dueTime: string | null;
  state: 'overdue' | 'today';
  /** Wiederkehrend: Abhaken rückt den Tag weiter, „Rückgängig“ gibt es dann nicht. */
  repeats: boolean;
  /** Sterne fürs Erledigen — Geld (`rewardCents`) gehört nie auf die Anzeige. */
  stars: number | null;
}

export interface BoardPerson {
  /** `null` = niemandem zugewiesen („Für alle“). */
  userId: string | null;
  name: string | null;
  child: boolean;
  tasks: BoardTask[];
}

export interface BoardStars {
  userId: string;
  name: string;
  balance: number;
  goal: { name: string; stars: number } | null;
}

export interface FamilyBoard {
  today: BoardEvent[];
  tomorrow: BoardEvent[];
  /** Je Mitglied die heute fälligen und überfälligen Aufgaben — nur wer etwas hat. */
  people: BoardPerson[];
  shopping: { open: number; names: string[] };
  stars: BoardStars[];
}

export interface BoardRows {
  householdEvents: readonly LocalHouseholdEvent[];
  tasks: readonly LocalTask[];
  taskLists: readonly LocalTaskList[];
  members: readonly LocalHouseholdMember[];
  shopping: readonly LocalShoppingItem[];
  stars: readonly LocalStarEntry[];
}

const alive = <T extends { deletedAt: string | null }>(rows: readonly T[]) =>
  rows.filter((row) => !row.deletedAt);

/** Reine Auswahl — alles außerhalb von `householdId` fällt heraus. */
export function familyBoard(
  rows: BoardRows,
  context: { householdId: string; today: string },
): FamilyBoard {
  const { householdId, today } = context;
  const tomorrow = addDays(today, 1);
  const members = alive(rows.members).filter((member) => member.householdId === householdId);
  const nameOf = new Map(members.map((member) => [member.userId, member.displayName]));

  const eventsOn = (day: string): BoardEvent[] =>
    alive(rows.householdEvents)
      .filter(
        (row) =>
          row.householdId === householdId && row.date <= day && (row.endDate ?? row.date) >= day,
      )
      .sort(
        (a, b) =>
          (a.time ?? '').localeCompare(b.time ?? '') || a.title.localeCompare(b.title, 'de'),
      )
      .map((row) => ({
        id: row.id,
        title: row.title,
        date: row.date,
        // Mehrtägiges ist an den Folgetagen ganztägig.
        time: row.date === day ? row.time : null,
        location: row.location,
        cancelled: row.cancelled,
        forNames: (row.forUserIds ?? [])
          .map((id) => nameOf.get(id))
          .filter((name): name is string => Boolean(name)),
      }));

  // Nur geteilte Listen dieses Haushalts — privat bleibt privat, auch vor der Küche.
  const sharedLists = new Set(
    alive(rows.taskLists)
      .filter((list) => list.householdId === householdId && list.kind !== 'checklist')
      .map((list) => list.id),
  );
  const due = alive(rows.tasks)
    .filter(
      (task) =>
        task.householdId === householdId &&
        sharedLists.has(task.listId) &&
        !task.doneAt &&
        task.dueDate !== null &&
        task.dueDate <= today,
    )
    .sort(
      (a, b) =>
        a.dueDate!.localeCompare(b.dueDate!) ||
        (a.dueTime ?? '').localeCompare(b.dueTime ?? '') ||
        a.title.localeCompare(b.title, 'de'),
    );
  const byPerson = new Map<string | null, BoardTask[]>();
  for (const task of due) {
    // Zuständige außerhalb des Haushalts gibt es nicht — wenn doch, „für alle“.
    const owner = task.assigneeId && nameOf.has(task.assigneeId) ? task.assigneeId : null;
    const list = byPerson.get(owner) ?? [];
    list.push({
      id: task.id,
      title: task.title,
      dueDate: task.dueDate!,
      dueTime: task.dueTime,
      state: taskState(task, today) === 'overdue' ? 'overdue' : 'today',
      repeats: task.repeat !== null,
      stars: task.rewardStars ?? null,
    });
    byPerson.set(owner, list);
  }
  const people: BoardPerson[] = members
    .filter((member) => byPerson.has(member.userId))
    .map((member) => ({
      userId: member.userId,
      name: member.displayName,
      child: member.role === 'child',
      tasks: byPerson.get(member.userId)!,
    }));
  const unassigned = byPerson.get(null);
  if (unassigned) people.push({ userId: null, name: null, child: false, tasks: unassigned });

  // In Regalfolge wie die Liste selbst — die ersten Namen sind die, die man zuerst sieht.
  const open = alive(rows.shopping)
    .filter((item) => item.householdId === householdId && !item.checkedAt)
    .sort(
      (a, b) =>
        shelfOrder(a.category) - shelfOrder(b.category) || a.name.localeCompare(b.name, 'de'),
    );

  const starRows = alive(rows.stars).filter((entry) => entry.householdId === householdId);
  const stars: BoardStars[] = members
    .filter((member) => member.role === 'child')
    .map((member) => {
      const own = starRows.filter((entry) => entry.childId === member.userId);
      return {
        member,
        own,
        balance: starBalance(own),
      };
    })
    // Wie im Taschengeld-Reiter: Sterne erst, wenn sie an sind oder welche da sind.
    .filter(({ member, own }) => member.taskStars === true || own.length > 0)
    .map(({ member, balance }) => ({
      userId: member.userId,
      name: member.displayName,
      balance,
      goal:
        member.starGoalName && member.starGoalStars
          ? { name: member.starGoalName, stars: member.starGoalStars }
          : null,
    }));

  return {
    today: eventsOn(today),
    tomorrow: eventsOn(tomorrow),
    people,
    shopping: { open: open.length, names: open.slice(0, BOARD_SHOPPING_NAMES).map((i) => i.name) },
    stars,
  };
}

/**
 * Für `useLiveQuery`: liest **flach** in einem Schritt (CLAUDE.md) — der
 * Haushalt und „heute“ werden hineingereicht, nicht hier nachgeschlagen.
 */
export async function readFamilyBoard(
  householdId: string,
  today: string,
  database: KraftwerkDatabase = db,
): Promise<FamilyBoard> {
  const [householdEvents, tasks, taskLists, members, shopping, stars] = await Promise.all([
    database.household_events.where('householdId').equals(householdId).toArray(),
    database.tasks.where('householdId').equals(householdId).toArray(),
    database.task_lists.where('householdId').equals(householdId).toArray(),
    database.household_members.where('householdId').equals(householdId).toArray(),
    database.shopping_items.where('householdId').equals(householdId).toArray(),
    database.star_entries.where('householdId').equals(householdId).toArray(),
  ]);
  return familyBoard(
    { householdEvents, tasks, taskLists, members, shopping, stars },
    { householdId, today },
  );
}

// --- Was ein Abhaken am Tablet bucht (27.10.2026) ----------------------------

const BOARD_REWARDS_KEY = 'familyBoardRewards';

/**
 * Zählt ein Abhaken an der Familienanzeige für Sterne und Aufgaben-Geld des
 * zuständigen Kindes? **Je Gerät** (das Tablet), ab Werk ja. Die Grenze zieht
 * trotzdem der Server: Bei „nein“ trägt der Erledigt-Eintrag `noReward`, und
 * `bookRewards`/`bookStars` lassen ihn aus. Erledigt ist die Aufgabe so oder so,
 * „erledigt von“ bleibt die zuständige Person.
 */
export async function readBoardRewards(database: KraftwerkDatabase = db): Promise<boolean> {
  return (await database.meta.get(BOARD_REWARDS_KEY))?.value !== 'nein';
}

export async function writeBoardRewards(
  counts: boolean,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: BOARD_REWARDS_KEY, value: counts ? 'ja' : 'nein' });
}
